import { areDevTogglesEnabled } from 'constants/config';

export const MSW_MOCKS_STORAGE_KEY = 'ENV0_MSW_MOCKS';
export const MSW_MOCKS_MODE_STORAGE_KEY = 'ENV0_MSW_MOCKS_MODE';

const SHARED_MOCK_MODES = {
  'with-data': 'With data',
  'zero-states': 'Zero states',
  // A connected organization whose member lacks the permission the data endpoint requires: the
  // integration itself reads fine, every data query is refused. Reproduces ATH-332 without a
  // second account.
  'no-permissions': 'No permissions'
} as const;

export const MOCK_MODES = {
  ...SHARED_MOCK_MODES,
  'free-with-data': 'Free plan, with data',
  'contracted-zero-states': 'Contracted plan, zero states'
} as const;

export type MockMode = keyof typeof MOCK_MODES;

/* One section per integration the mocks cover, so the dev panel can grow a section per area rather
   than a flat list of modes. The mode is one global setting shared by every section. */
export type MockSection = { key: string; title: string; modes?: Partial<Record<MockMode, string>> };

export const MOCK_SECTIONS: MockSection[] = [
  {
    key: 'env0',
    title: 'env0',
    modes: {
      'with-data': 'Contracted plan, with data',
      'free-with-data': 'Free plan, with data',
      'contracted-zero-states': 'Contracted plan, zero states',
      'zero-states': 'Free plan, zero states',
      'no-permissions': 'No permissions'
    }
  },
  { key: 'cloudquery', title: 'CloudQuery', modes: SHARED_MOCK_MODES }
];

const DEFAULT_MOCK_MODE: MockMode = 'with-data';

export const areMocksEnabled = (): boolean =>
  areDevTogglesEnabled && localStorage.getItem(MSW_MOCKS_STORAGE_KEY) === 'true';

export const getMockMode = (): MockMode => {
  const mode = localStorage.getItem(MSW_MOCKS_MODE_STORAGE_KEY);

  return mode && mode in MOCK_MODES ? (mode as MockMode) : DEFAULT_MOCK_MODE;
};

export const isZeroStatesMode = (): boolean => getMockMode() === 'zero-states';

export const isNoPermissionsMode = (): boolean => getMockMode() === 'no-permissions';

export const startMocks = async (): Promise<void> => {
  if (!areMocksEnabled()) return;

  const [{ setupWorker }, { handlers }] = await Promise.all([import('msw/browser'), import('./handlers')]);

  await setupWorker(...handlers).start({ onUnhandledRequest: 'bypass', quiet: true });
};
